import React, { useCallback, useEffect } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useTellYourStoryPrompt } from '@/organisms/TellYourStoryPrompt/TellYourStoryPrompt'
import { Offer } from '@/services/PifService'
import { getStripe } from '@/services/StripeService/StripePaymentService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useClientSecret } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useClientSecret'
import { PayItForwardFormProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'
import { usePayItForwardState } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/usePayItForwardState'
import { DavidStripeForm } from './DavidStripeForm'

export interface DavidPaymentMethodsProps {
  offer: Offer
  isShowThanksForSharing: boolean
  className?: string
}

export const DavidPaymentMethods: React.FC<DavidPaymentMethodsProps> = ({ offer, isShowThanksForSharing }) => {
  const { locale } = useLocale()
  const { onComplete: onCompletePifState } = usePayItForwardState()
  const { openModal: openTellYourStoryModal, setRelatedOrderId } = useTellYourStoryPrompt()
  const { user } = useUser()
  const { clientSecret, loading } = useClientSecret({ offer })
  const { push } = useSafeRouter()
  const { isPayItForwardComplete } = usePayItForwardState()

  useEffect(() => {
    if (isPayItForwardComplete) {
      push(`${paths.payItForward.index}/david/checkout/${offer.id}?order=complete`)
    }
  }, [isPayItForwardComplete, push, offer.id])

  const openTellYourStoryModalWhenApplicable = useCallback(() => {
    if (isShowThanksForSharing && Boolean(user)) {
      openTellYourStoryModal()
    }
  }, [isShowThanksForSharing, openTellYourStoryModal, user])

  const onComplete = useCallback(
    ({ orderId }: { orderId: string }) => {
      onCompletePifState()
      setRelatedOrderId(orderId)
      openTellYourStoryModalWhenApplicable()
    },
    [onCompletePifState, openTellYourStoryModalWhenApplicable, setRelatedOrderId],
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinnerIcon size={48} color="gray-600" />
      </div>
    )
  }

  if (clientSecret) {
    return (
      <Elements key={clientSecret} stripe={getStripe(locale)} options={{ loader: 'auto', clientSecret }}>
        <PayItForwardFormProvider onComplete={onComplete} offer={offer} projectSlug={slugs.david} user={user}>
          <DavidStripeForm clientSecret={clientSecret} offer={offer} />
        </PayItForwardFormProvider>
      </Elements>
    )
  }

  return null
}
